import { Injectable, computed, signal } from '@angular/core';
import { Observable, delay, of, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { AccesoUsuario, Credenciales, EstadoSesion, SesionUsuario } from './auth.types';

/**
 * Implementacion SIMULADA del contrato.
 *
 * Existe para poder trabajar la pantalla sin levantar ApiUserManager ni la base.
 * NO es codigo de produccion: `app.config.ts` la provee solo cuando
 * `environment.authSimulado` es true, ese flag es false en `environment.prod.ts`
 * y `app.config.ts` LANZA si las dos cosas coinciden. R5 de `pnpm run audit` lo
 * verifica.
 *
 * Lo que NO hace, a proposito:
 *   - No inventa un JWT. La app real nunca ve un token, asi que simular uno
 *     crearia parseo que parece probado y jamas vio un token de verdad.
 *   - No usa `localStorage`. Usa `sessionStorage`, que muere con la pestaña,
 *     para que ninguna sesion falsa sobreviva a cerrar el navegador.
 */

const CLAVE_SESION = 'auth.simulada.estado';

/**
 * Las tarjetas de prueba replican los tres casos que importan:
 *
 *   `demo`  → DOS accesos, que es lo unico que pinta la rejilla.
 *   `uno`   → UNO, para ejercitar la autoseleccion.
 *   `nuevo` → con `debeCambiarContrasena`, el caso de 125 de los 221 reales.
 *
 * Las urls son de `.invalid`, el TLD que la RFC 2606 reserva para esto: asi un
 * salto accidental en desarrollo no sale a ningun sitio real. Y sirven de lista
 * blanca para `destinoSeguro()` igual que las de verdad.
 */
interface CuentaSimulada {
  readonly usuario: string;
  readonly password: string;
  readonly perfil: SesionUsuario;
  readonly accesos: readonly AccesoUsuario[];
}

const ACCESO_FACTURACION: AccesoUsuario = {
  rolCodigo: 'ejecutivo',
  rolNombre: 'Ejecutivo',
  aplicacionCodigo: 'facturacion',
  nombre: 'Facturación',
  icono: 'file-done',
  url: 'https://facturacion.sinay.invalid',
  esSuperadmin: false,
};

const ACCESO_TESORERIA: AccesoUsuario = {
  rolCodigo: 'administrador-tesorero',
  rolNombre: 'Administrador',
  aplicacionCodigo: 'tesoreria',
  nombre: 'Tesorería',
  icono: 'bank',
  url: 'https://tesoreria.sinay.invalid',
  esSuperadmin: false,
};

const CUENTAS: readonly CuentaSimulada[] = [
  {
    usuario: 'demo',
    password: 'demo',
    perfil: {
      id: 'u-001',
      login: 'demo',
      nombre: 'Usuario Demo',
      correo: 'demo@sinay.invalid',
      debeCambiarContrasena: false,
    },
    accesos: [ACCESO_FACTURACION, ACCESO_TESORERIA],
  },
  {
    usuario: 'uno',
    password: 'uno',
    perfil: {
      id: 'u-002',
      login: 'uno',
      nombre: 'Acceso Único',
      correo: 'uno@sinay.invalid',
      debeCambiarContrasena: false,
    },
    accesos: [ACCESO_FACTURACION],
  },
  {
    usuario: 'nuevo',
    password: 'nuevo',
    perfil: {
      id: 'u-003',
      login: 'nuevo',
      nombre: 'Contraseña Vencida',
      correo: 'nuevo@sinay.invalid',
      debeCambiarContrasena: true,
    },
    accesos: [ACCESO_FACTURACION, ACCESO_TESORERIA],
  },
];

/**
 * Latencia fingida. Sin ella los estados de carga nunca se ejercitan y el
 * spinner del boton se descubre roto el dia que se conecta el backend.
 */
const LATENCIA_MS = 600;

@Injectable()
export class AuthSimuladoService extends AuthService {
  private readonly estado = signal<EstadoSesion | null>(null);

  readonly sesion = computed(() => this.estado()?.usuario ?? null);
  readonly autenticado = computed(() => this.estado() !== null);
  readonly accesos = computed<readonly AccesoUsuario[]>(() => this.estado()?.accesos ?? []);
  readonly accesoActivo = computed(() => this.estado()?.accesoActivo ?? null);

  iniciarSesion(credenciales: Credenciales): Observable<void> {
    const cuenta = CUENTAS.find(
      (c) =>
        c.usuario === credenciales.usuario.trim().toLowerCase() &&
        c.password === credenciales.password,
    );

    return of(null).pipe(
      delay(LATENCIA_MS),
      switchMap(() => {
        if (!cuenta) {
          // MISMO error para usuario inexistente y contraseña equivocada.
          // Distinguirlos convierte el login en un enumerador de cuentas.
          return throwError(() => new Error('Usuario o contraseña incorrectos.'));
        }
        this.guardar({ usuario: cuenta.perfil, accesos: cuenta.accesos, accesoActivo: null });
        return of(undefined);
      }),
    );
  }

  elegirAcceso(acceso: AccesoUsuario): Observable<AccesoUsuario> {
    return of(null).pipe(
      delay(LATENCIA_MS / 2),
      switchMap(() => {
        const actual = this.estado();
        if (!actual) return throwError(() => new Error('La sesión no es válida.'));
        this.guardar({ ...actual, accesoActivo: acceso });
        return of(acceso);
      }),
    );
  }

  cambiarContrasena(actual: string, nueva: string): Observable<void> {
    return of(null).pipe(
      delay(LATENCIA_MS),
      switchMap(() => {
        const sesion = this.estado();
        if (!sesion) return throwError(() => new Error('La sesión no es válida.'));
        const cuenta = CUENTAS.find((c) => c.usuario === sesion.usuario.login);
        if (!cuenta || cuenta.password !== actual) {
          return throwError(() => new Error('La contraseña actual no es correcta.'));
        }
        if (nueva === actual) {
          return throwError(() => new Error('La contraseña nueva debe ser distinta.'));
        }
        // Se apaga la bandera, igual que hace el backend al devolver la sesion
        // renovada. Los accesos se conservan: la rejilla se pinta con esto.
        this.guardar({
          ...sesion,
          usuario: { ...sesion.usuario, debeCambiarContrasena: false },
        });
        return of(undefined);
      }),
    );
  }

  cerrarSesion(): Observable<void> {
    this.estado.set(null);
    sessionStorage.removeItem(CLAVE_SESION);
    return of(undefined).pipe(delay(LATENCIA_MS / 2));
  }

  restaurarSesion(): Observable<void> {
    try {
      const guardado = sessionStorage.getItem(CLAVE_SESION);
      if (guardado) this.estado.set(JSON.parse(guardado) as EstadoSesion);
    } catch {
      // Un `sessionStorage` bloqueado o un JSON corrupto no pueden tumbar el
      // arranque: se empieza sin sesion, que es el estado seguro.
      this.estado.set(null);
    }
    return of(undefined);
  }

  private guardar(estado: EstadoSesion): void {
    this.estado.set(estado);
    try {
      sessionStorage.setItem(CLAVE_SESION, JSON.stringify(estado));
    } catch {
      // Sin persistencia la sesion simulada no sobrevive a un F5. Es una
      // molestia en desarrollo, no un fallo.
    }
  }
}
